import { fromEvent, map, withLatestFrom } from 'rxjs';
import { store } from '@redux/store';
import ImpressionEventObserver from 'utils/impressionEventObserver';
import { googleAnalytics4Impression, googleAnalytics4Event } from 'features/googleAnalytics4';
import { getAnalyticsTrackingProperties } from 'features/trackingProperties';
import {
  selectModal$,
  ModalStep,
  selectSelectedSlot,
  getGoogleAnalyticsEventProperties,
  getSourceRequestKey
} from 'features/selfGuidedTour';
import {
  mapInteractionOccurred$,
  panelUpdatedOccurred$,
  panelScrollOccurred$,
  drawerDraggedOccurred$,
  lotSelectedOccurred$,
  cardsScrolledOccurred$,
  tabSelectedOccurred$,
  notifyWhenAvailableClickedOccurred$,
  floorPlanViewedOccurred$,
  viewFloorPlanClickedOccurred$,
  emptyConfigViewedOccurred$,
  similarHomesToggledOccurred$,
  similarHomeCardClickedOccurred$,
  soldSeeAllClickedOccurred$,
  detailViewOpenedOccurred$,
  detailsTabSelectedOccurred$,
  changeLotSelectedOccurred$,
  exteriorSelectedOccurred$,
  exteriorSelectorExpandedOccurred$,
  mobileLotMapClickedOccurred$,
  mobileLotCardNavigationOccurred$,
  filterPanelOpenedOccurred$,
  filterPanelClosedOccurred$,
  filterUpdatedOccurred$,
  filtersAppliedOccurred$,
  filtersClearedOccurred$,
  buildGoogleAnalytics4MapInteractionEvent,
  buildGoogleAnalytics4PanelUpdatedEvent,
  buildGoogleAnalytics4PanelScrollEvent,
  buildGoogleAnalytics4DrawerDraggedEvent,
  buildGoogleAnalytics4LotSelectedEvent,
  buildGoogleAnalytics4CardsScrolledEvent,
  buildGoogleAnalytics4TabSelectedEvent,
  buildGoogleAnalytics4NotifyWhenAvailableClickedEvent,
  buildGoogleAnalytics4FloorPlanViewedEvent,
  buildGoogleAnalytics4ViewFloorPlanClickedEvent,
  buildGoogleAnalytics4EmptyConfigViewedEvent,
  buildGoogleAnalytics4SimilarHomesToggledEvent,
  buildGoogleAnalytics4SimilarHomeCardClickedEvent,
  buildGoogleAnalytics4SoldSeeAllClickedEvent,
  buildGoogleAnalytics4DetailViewOpenedEvent,
  buildGoogleAnalytics4DetailsTabSelectedEvent,
  buildGoogleAnalytics4ChangeLotSelectedEvent,
  buildGoogleAnalytics4ExteriorSelectedEvent,
  buildGoogleAnalytics4ExteriorSelectorExpandedEvent,
  buildGoogleAnalytics4MobileLotMapClickedEvent,
  buildGoogleAnalytics4MobileLotCardNavigationEvent,
  buildGoogleAnalytics4FilterPanelOpenedEvent,
  buildGoogleAnalytics4FilterPanelClosedEvent,
  buildGoogleAnalytics4FilterUpdatedEvent,
  buildGoogleAnalytics4FiltersAppliedEvent,
  buildGoogleAnalytics4FiltersClearedEvent,
} from 'features/nativeInteractiveSiteMap';
import {
  leadOptionSelectedOccurred$,
  leadFormSubmittedOccurred$,
  leadFormClosedOccurred$,
  leadFormAbandonedOccurred$,
  leadFormSubmissionFailedOccurred$,
  notifyFormOpenedOccurred$,
  notifySubmitClickedOccurred$,
  notifyModalClosedOccurred$,
  notifyAbandonedOccurred$,
  notifySubmissionFailedOccurred$,
  buildGoogleAnalytics4FormOpenEvent,
  buildGoogleAnalytics4GenerateLeadEvent,
  buildGoogleAnalytics4FormCloseEvent,
  buildGoogleAnalytics4LeadFormAbandonedEvent,
  buildGoogleAnalytics4LeadFormSubmissionFailedEvent,
} from 'features/leadGenerators';
import withPosition from '../../services/contactBuilderPosition';

const uTourDialogElement = document.querySelector('[data-self-guided-tour-dialog]');
const uTourCtasButtons = document.querySelectorAll('[data-self-guided-tour-trigger]');

/**
 * @function mapGlobalPropertiesToSegment
 * @description Maps global properties to a Segment tracking object.
 * @returns {Object} An object containing the mapped properties for Segment tracking.
 */
function mapGlobalPropertiesGA4() {
  const props = getAnalyticsTrackingProperties();
  return {
    brand_partner_name: props.brand_partner_name,
    page_type: props.page_type,
    partner_id: props.partner_id,
    partner_name: props.partner_name,
    brand_partner_name_partner_name: props.brandPartnerNamePartnerName,
    time: props.time,
    refer_code: props.referCode,
    // event_type: props.eventType,
    // currency_value: props.currencyValue,
    // value: props.value,
    // max_price: props.maxPrice,
    home_status: props.home_type,
    amenities: props.amenities,
    builder_name: props.builder_name,
    community_name: props.community_name,
    community_status: props.communityType,
    trustbuilder_reviews: props.trustBuilderReviews,
    location: props.location,
    is_priced: props.isPriced,
    has_ifp: props.hasIfp,
    primary_image: props.primary_image_url,
    brand_name: props.brand_name,
    brand_id: props.brand_id,
    builder_corp: props.builder_name,
    community_id: props.communityId,
    builder_id: props.builder_id,
    county: props.county,
    builder_division: props.builder_division,
    market_session: props.marketSession,
    has_isp: props.hasIsp,
    home_price: props.home_price,
    zip_code: props.zip,
    price_reduction: props.price_reduction,
  };
}

/**
 * @function sendUtourEvent
 * @description Dispatches a GA4 analytics event for self-guided tour interactions.
 *              Combines shared and custom properties, then sends the event to the Redux store.
 * @param {Object} data - The custom event properties specific to the user interaction.
 * @param {string} [eventName="utour_clicks"] - The GA4 event name to dispatch.
 */
const sendUtourEvent = (data, eventName = 'utour_clicks') => {
  if (!data) {
    return;
  }

  const sharedProperties = mapGlobalPropertiesGA4();
  const customProperties = {
    ...sharedProperties,
    ...data,
  };

  store.dispatch(googleAnalytics4Event({ eventName, customProperties }));
};

/**
 * @function triggerBannerButtonClick
 * @description Handles the click event on the self-guide utour button.
 * @param {Event} event - The HTML event object.
 */
const triggerBannerButtonClick = function (event) {
  const sectionName = event.target.closest('[data-your-self-guided-home-tour]')
    ? 'Your self-guided home tour'
    : 'Request a tour today!';

  const buttonName = event.currentTarget.dataset.selfGuidedParticipant ? 'ScheduleSelfTour' : 'SelfGuidedTour';

  const customProperties = {
    click_target2: sectionName,
    button_name: buttonName,
    position: 'CommunityDetails',
  };

  sendUtourEvent(customProperties);
};

// Observable to handle click on self-guides banner utour button.
uTourCtasButtons.forEach((element) => fromEvent(element, 'click').subscribe(triggerBannerButtonClick));

/**
 * triggerUtourCtaEvent: Triggers the Book a self-guided tour CTAs impression event.
 * @param {HTMLElement} ButtonCTA  Book a self-guided tour CTA.
 */
const triggerUtourCtaEvent = (event) => {
  const sectionName = event.closest('[data-your-self-guided-home-tour]')
    ? 'Your self-guided home tour'
    : 'Request a tour today!';

  const buttonName = event.dataset.selfGuidedParticipant ? 'ScheduleSelfTour' : 'SelfGuidedTour';

  const analyticsProperties = mapGlobalPropertiesGA4();

  const customProperties = {
    ...analyticsProperties,
    click_target2: sectionName,
    button_name: buttonName,
    position: 'CommunityDetails',
  };

  store.dispatch(
    googleAnalytics4Impression({
      eventName: 'utour_impression',
      customProperties,
    }),
  );
};

const uTourCtaEventObserver = new ImpressionEventObserver(triggerUtourCtaEvent);

/**
 * @function bindUtourCtaEventEvents
 * @description Attaches intersection observers to all self-guided tour CTA buttons.
 *              Used to track impressions for each CTA by observing when they enter the viewport.
 */
const bindUtourCtaEventEvents = () => {
  const UtourCtaObserver = uTourCtaEventObserver.getIntersectionObserver();
  Array.from(uTourCtasButtons).forEach((card) => UtourCtaObserver.observe(card));
};

bindUtourCtaEventEvents();

function sendConfirmationStepEvent({ currentStep }) {
  if (currentStep === ModalStep.Confirmation.toString()) {
    const properties = {
      position: 'Rft',
      button_name: 'ScheduleMySelfTour',
      click_target2: new Date().toISOString(),
    };
    
    const tourInformation = selectSelectedSlot(store.getState());
    const homeElements = document.querySelectorAll('[data-self-guided-tour-homes-item]');
    
    if (tourInformation) {
      properties.click_target3 = tourInformation.SpecTimezoneDate;
      properties.other_filters = homeElements.length;
    }

    properties.source_request_key = getSourceRequestKey();

    sendUtourEvent(properties);
  }
}

function sendLoginRegisterEvents({ currentStep, previousStep }) {
  if (currentStep === ModalStep.SignIn.toString() || currentStep === ModalStep.SignUp.toString()) {
    return;
  }

  if (previousStep === ModalStep.SignIn.toString() || previousStep === ModalStep.SignUp.toString()) {
    const buttonName = previousStep === ModalStep.SignIn.toString() ? 'Sign in' : 'Sign up';
    sendUtourEvent({
      button_name: buttonName,
      position: 'Register',
    });
  }
}

selectModal$.subscribe(sendConfirmationStepEvent);
selectModal$.subscribe(sendLoginRegisterEvents);

/**
 * Sets up an observable that listens for click events on the self-guided tour dialog element.
 * Each click event is combined with the latest modal step context, mapped to analytics event properties,
 * and then dispatched using sendUtourEvent for GA4 tracking.
 */
if (uTourDialogElement) {
  fromEvent(uTourDialogElement, 'click')
    .pipe(
      withLatestFrom(selectModal$),
      map(([event, { currentStep, previousStep }]) => ({
        event,
        currentStep,
        previousStep,
      })),
      map(getGoogleAnalyticsEventProperties),
    )
    .subscribe(sendUtourEvent);
}

/**
 * @function mapGlobalPropertiesToNativeInteractiveSiteMapGA4
 * @description Global properties merged into every Native ISM GA4 event — a subset of the field
 * list mapGlobalPropertiesGA4 above uses (only the ones Native ISM's tables ask for).
 * @returns {Object}
 */
function mapGlobalPropertiesToNativeInteractiveSiteMapGA4() {
  const props = getAnalyticsTrackingProperties();
  return {
    community_id: props.communityId,
    community_name: props.community_name,
    builder_id: props.builder_id,
    builder_corp: props.builder_name,
    county: props.county,
    zip_code: props.zip,
  };
}

/**
 * @function dispatchNativeInteractiveSiteMapGoogleAnalytics4Event
 * @description Merges global properties into a built Native ISM event and dispatches it via
 * googleAnalytics4Event. The ISM feature has no analytics knowledge of its own; it only emits raw
 * payloads on the Subjects below (see features/nativeInteractiveSiteMap/redux/observables.js).
 * @param {{ eventName: string, customProperties: Object }} builtEvent
 */
function dispatchNativeInteractiveSiteMapGoogleAnalytics4Event({ eventName, customProperties }) {
  store.dispatch(
    googleAnalytics4Event({
      eventName,
      customProperties: { ...mapGlobalPropertiesToNativeInteractiveSiteMapGA4(), ...customProperties },
    }),
  );
}

mapInteractionOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4MapInteractionEvent(payload)),
);
panelUpdatedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4PanelUpdatedEvent(payload)),
);
panelScrollOccurred$.subscribe(() =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4PanelScrollEvent()),
);
drawerDraggedOccurred$.subscribe(() =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4DrawerDraggedEvent()),
);
lotSelectedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4LotSelectedEvent(payload)),
);
cardsScrolledOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4CardsScrolledEvent(payload)),
);
tabSelectedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4TabSelectedEvent(payload)),
);
notifyWhenAvailableClickedOccurred$.subscribe(() =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4NotifyWhenAvailableClickedEvent()),
);
floorPlanViewedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4FloorPlanViewedEvent(payload)),
);
viewFloorPlanClickedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4ViewFloorPlanClickedEvent(payload)),
);
emptyConfigViewedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4EmptyConfigViewedEvent(payload)),
);
similarHomesToggledOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4SimilarHomesToggledEvent(payload)),
);
similarHomeCardClickedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4SimilarHomeCardClickedEvent(payload)),
);
soldSeeAllClickedOccurred$.subscribe(() =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4SoldSeeAllClickedEvent()),
);

/**
 * @function dispatchLeadFormGoogleAnalytics4Event
 * @description Dispatches a built Contact Builder lead-form event via googleAnalytics4Event. The
 * leadGenerators feature has no analytics knowledge of its own — it only emits raw payloads
 * (carrying `fromPage`, not `position`) on the Subjects below; withPosition (see
 * ../../services/contactBuilderPosition.js) translates that into this page's `position` value before the
 * payload reaches a build*Event function, since leadGenerators is shared and has no business
 * knowing what "ISM" means. Unlike the Native ISM events above, these carry no page-level global
 * properties — the ticket's (NHS20-8253) property list is fully self-contained per event.
 * @param {{ eventName: string, customProperties: Object }} builtEvent
 */
function dispatchLeadFormGoogleAnalytics4Event({ eventName, customProperties }) {
  store.dispatch(googleAnalytics4Event({ eventName, customProperties }));
}

leadOptionSelectedOccurred$.subscribe((payload) =>
  dispatchLeadFormGoogleAnalytics4Event(buildGoogleAnalytics4FormOpenEvent(withPosition(payload))),
);
leadFormSubmittedOccurred$.subscribe((payload) =>
  dispatchLeadFormGoogleAnalytics4Event(buildGoogleAnalytics4GenerateLeadEvent(withPosition(payload))),
);
leadFormClosedOccurred$.subscribe((payload) =>
  dispatchLeadFormGoogleAnalytics4Event(buildGoogleAnalytics4FormCloseEvent(withPosition(payload))),
);
leadFormAbandonedOccurred$.subscribe((payload) =>
  dispatchLeadFormGoogleAnalytics4Event(buildGoogleAnalytics4LeadFormAbandonedEvent(withPosition(payload))),
);
leadFormSubmissionFailedOccurred$.subscribe((payload) =>
  dispatchLeadFormGoogleAnalytics4Event(buildGoogleAnalytics4LeadFormSubmissionFailedEvent(withPosition(payload))),
);
detailViewOpenedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4DetailViewOpenedEvent(payload)),
);
detailsTabSelectedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4DetailsTabSelectedEvent(payload)),
);
changeLotSelectedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4ChangeLotSelectedEvent(payload)),
);
exteriorSelectedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4ExteriorSelectedEvent(payload)),
);
exteriorSelectorExpandedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(
    buildGoogleAnalytics4ExteriorSelectorExpandedEvent(payload),
  ),
);
mobileLotMapClickedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4MobileLotMapClickedEvent(payload)),
);
mobileLotCardNavigationOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4MobileLotCardNavigationEvent(payload)),
);
filterPanelOpenedOccurred$.subscribe(() =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4FilterPanelOpenedEvent()),
);
filterPanelClosedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4FilterPanelClosedEvent(payload)),
);
filterUpdatedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4FilterUpdatedEvent(payload)),
);
filtersAppliedOccurred$.subscribe((payload) =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4FiltersAppliedEvent(payload)),
);
filtersClearedOccurred$.subscribe(() =>
  dispatchNativeInteractiveSiteMapGoogleAnalytics4Event(buildGoogleAnalytics4FiltersClearedEvent()),
);

/**
 * "Get on the list" (Coming Soon "Get Notified") events (NHS20-9093). Reuses the exact same
 * build*Event functions as the Contact Builder events above — the ticket's tracking plan mirrors
 * Contact Builder's event vocabulary verbatim for this form, just with a fixed 'Get on the list'
 * button_name (see services/getOnTheListTracking.js).
 */
notifyFormOpenedOccurred$.subscribe((payload) =>
  dispatchLeadFormGoogleAnalytics4Event(buildGoogleAnalytics4FormOpenEvent(withPosition(payload))),
);
notifySubmitClickedOccurred$.subscribe((payload) =>
  dispatchLeadFormGoogleAnalytics4Event(buildGoogleAnalytics4GenerateLeadEvent(withPosition(payload))),
);
notifyModalClosedOccurred$.subscribe((payload) =>
  dispatchLeadFormGoogleAnalytics4Event(buildGoogleAnalytics4FormCloseEvent(withPosition(payload))),
);
notifyAbandonedOccurred$.subscribe((payload) =>
  dispatchLeadFormGoogleAnalytics4Event(buildGoogleAnalytics4LeadFormAbandonedEvent(withPosition(payload))),
);
notifySubmissionFailedOccurred$.subscribe((payload) =>
  dispatchLeadFormGoogleAnalytics4Event(buildGoogleAnalytics4LeadFormSubmissionFailedEvent(withPosition(payload))),
);
